import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { registerParticipant } from '@/lib/api';
import { RTBLogo } from '@/components/RTBLogo';

export default function Register() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', password: '', confirm_password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const { token } = await registerParticipant(form);
      localStorage.setItem('rtb_user_token', token);
      toast.success("Account created! Let's get started.");
      navigate('/portal');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-rtb-mint/30 flex items-center justify-center px-4 py-12" data-testid="register-page">
      <div className="fixed top-20 left-20 w-72 h-72 bg-rtb-purple/5 rounded-full blur-[100px]" />
      <div className="fixed bottom-20 right-20 w-72 h-72 bg-rtb-green/5 rounded-full blur-[100px]" />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-md w-full relative z-10">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <RTBLogo className="h-10 mx-auto" />
          </Link>
          <h1 className="text-3xl font-oswald font-bold uppercase text-gray-900">
            Start Your <span className="text-rtb-purple">Journey</span>
          </h1>
          <p className="text-gray-500 text-sm mt-2">Create your account to begin the 20-day program</p>
        </div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-light p-8 space-y-4 rounded-2xl"
        >
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-600 mb-1.5 block text-sm font-medium">First Name</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="input-styled" required data-testid="reg-first-name" />
            </div>
            <div>
              <Label className="text-gray-600 mb-1.5 block text-sm font-medium">Last Name</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="input-styled" required data-testid="reg-last-name" />
            </div>
          </div>
          <div>
            <Label className="text-gray-600 mb-1.5 block text-sm font-medium">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="input-styled" required data-testid="reg-email" />
          </div>
          <div>
            <Label className="text-gray-600 mb-1.5 block text-sm font-medium">Phone</Label>
            <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="input-styled" required data-testid="reg-phone" />
          </div>
          <div className="relative">
            <Label className="text-gray-600 mb-1.5 block text-sm font-medium">Password</Label>
            <Input type={showPassword ? "text" : "password"} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="input-styled pr-12" required data-testid="reg-password" />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-[38px] text-gray-400 hover:text-gray-600">
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <div>
            <Label className="text-gray-600 mb-1.5 block text-sm font-medium">Confirm Password</Label>
            <Input type="password" value={form.confirm_password}
              onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
              className="input-styled" required data-testid="reg-confirm-password" />
          </div>

          <Button type="submit" disabled={loading} className="btn-primary w-full" data-testid="reg-submit-btn">
            {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> :
              <>Create Account <ArrowRight className="ml-2 w-5 h-5" /></>}
          </Button>

          <p className="text-center text-gray-400 text-sm">
            Already have an account? <Link to="/login" className="text-rtb-purple font-semibold hover:underline">Sign in</Link>
          </p>
        </motion.form>
      </motion.div>
    </div>
  );
}
